// Exposure ("Population baseline"): typical presence in an area from registers, not from phones.
// Figures come from the open data API (config/openData.ts). Without a base URL for it, or with
// REACT_APP_DRAFT_MOCKS=1, they come from a deterministic mock (services/draftMocks.ts).
import { AWAY_SHARE, BASELINE_CELL_CAP, CELL_SIZE_M, EXPOSURE_SOURCE, EXPOSURE_YEAR } from '../config/exposure';
import { openDataUrl } from '../config/openData';
import { draftBaselineCells, draftExposureBase, exposureMockEnabled } from './draftMocks';

/** Where a figure comes from: a register count, a rule applied to counts, or an assumption. */
export type ExposureTag = 'register' | 'rule' | 'assumption';

export type ExposureMoment = 'night' | 'weekday_day' | 'weekend_day' | 'evening';

export interface Exposure {
  residents: number;
  /** Employees at workplaces in the area. */
  workers: number;
  typical: Record<ExposureMoment, number>;
  year: number;
  sources: Array<{ name: string; licence: string; dataset: string }>;
  tags: Record<'residents' | 'workers' | ExposureMoment, ExposureTag>;
}

export interface ExposureRequest {
  geometries: string[];
  year?: number;
}

/**
 * Rule R2. Night and weekend day: residents. Weekday day: the residents who stay (1 − away share)
 * plus the workers. Evening: the mean of night and weekday day. Cabin users are not included yet.
 */
export function typicalPresence(residents: number, workers: number, awayShare: number = AWAY_SHARE): Record<ExposureMoment, number> {
  const night = residents;
  const weekdayDay = residents * (1 - awayShare) + workers;
  return {
    night: Math.round(night),
    weekday_day: Math.round(weekdayDay),
    weekend_day: Math.round(residents),
    evening: Math.round((night + weekdayDay) / 2),
  };
}

export function buildExposure(
  residents: number,
  workers: number,
  year: number,
  source: { name: string; licence: string } = EXPOSURE_SOURCE,
): Exposure {
  return {
    residents,
    workers,
    typical: typicalPresence(residents, workers),
    year,
    sources: [
      { name: source.name, licence: source.licence, dataset: `population 250 m ${year}` },
      { name: source.name, licence: source.licence, dataset: `workplaces 250 m ${year}` },
    ],
    tags: {
      residents: 'register',
      workers: 'register',
      night: 'rule',
      weekend_day: 'rule',
      weekday_day: 'assumption',
      evening: 'assumption',
    },
  };
}

/** POST a JSON body to an open data route; throws with the API's message on a non-2xx answer. */
async function postOpenData<T>(path: string, body: unknown): Promise<T> {
  const url = openDataUrl(path);
  if (!url) throw new Error('The open data API is not configured.');
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `The open data API answered ${res.status}.`;
    try {
      const err = await res.json();
      if (err && typeof err.error === 'string') message = err.error;
    } catch {
      /* keep the generic message */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

interface OpenDataSource {
  name: string;
  licence: string;
  url?: string;
  year?: number;
}

// The only caller of POST /api/demo/open-data/exposure.
async function fetchExposure(request: Required<ExposureRequest>): Promise<Exposure> {
  const r = await postOpenData<{ year: number; residents: number; employees: number; source: OpenDataSource }>('/exposure', {
    geometries: request.geometries,
    year: request.year,
  });
  return buildExposure(r.residents, r.employees, r.source.year ?? r.year, r.source);
}

export async function getExposure({ geometries, year = EXPOSURE_YEAR }: ExposureRequest): Promise<Exposure> {
  if (exposureMockEnabled()) {
    const { residents, workers } = draftExposureBase(geometries);
    return buildExposure(residents, workers, year);
  }
  return fetchExposure({ geometries, year });
}

// ── Population baseline cells ─────────────────────────────────────────────────────────────────
// The 250 m grid cells behind the totals, for drawing on the map. DRAFT CONTRACT, mocked the same
// way as getExposure.

export type BaselineMeasure = 'residents' | 'employees';

export interface BaselineCell {
  /** SSB's id of the 250 m cell. */
  ssbid: number;
  /** Registered residents, or employees at workplaces, in the cell. */
  value: number;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
}

export interface BaselineCellsRequest {
  geometries: string[];
  year?: number;
  measure: BaselineMeasure;
}

export interface BaselineCells {
  cells: BaselineCell[];
  year: number;
  source: { name: string; licence: string; dataset: string };
}

/** The area covers more cells than the layer draws. */
export class CellLayerTooLargeError extends Error {
  constructor() {
    super('Area too large for the cell layer');
    this.name = 'CellLayerTooLargeError';
  }
}

const CELL_DATASET: Record<BaselineMeasure, string> = {
  residents: EXPOSURE_SOURCE.population,
  employees: EXPOSURE_SOURCE.workplaces,
};

const CELL_ROUTE_DATASET: Record<BaselineMeasure, string> = { residents: 'population', employees: 'workplaces' };

// The only caller of POST /api/demo/open-data/{dataset}/cells.
async function fetchBaselineCells(request: Required<BaselineCellsRequest>): Promise<BaselineCells> {
  const r = await postOpenData<{ year: number; cells: BaselineCell[]; truncated: boolean; source: OpenDataSource }>(
    `/${CELL_ROUTE_DATASET[request.measure]}/cells`,
    { geometries: request.geometries, year: request.year, measure: request.measure },
  );
  if (r.truncated) throw new CellLayerTooLargeError();
  const year = r.source.year ?? r.year;
  return {
    cells: r.cells,
    year,
    source: { name: r.source.name, licence: r.source.licence, dataset: `${CELL_ROUTE_DATASET[request.measure]} 250 m ${year}` },
  };
}

export async function getBaselineCells({ geometries, year = EXPOSURE_YEAR, measure }: BaselineCellsRequest): Promise<BaselineCells> {
  if (exposureMockEnabled()) {
    const cells = draftBaselineCells(geometries, measure, CELL_SIZE_M, BASELINE_CELL_CAP);
    if (!cells) throw new CellLayerTooLargeError();
    return { cells, year, source: { name: EXPOSURE_SOURCE.name, licence: EXPOSURE_SOURCE.licence, dataset: CELL_DATASET[measure] } };
  }
  return fetchBaselineCells({ geometries, year, measure });
}
